/**
 * @fileoverview Pure helpers for the file-upload feature.
 *
 * Hidden behind the `fileUpload` feature flag. These functions are
 * side-effect free and independently unit-tested.
 */

import {
  File as FileIcon,
  FileText,
  FileImage,
  FileArchive,
  type LucideIcon,
} from "lucide-react";
import {
  getFileExtension,
  hasDisallowedFilenameChars,
  isAllowedUploadFile,
} from "@amzn/afxtaco-utilities";
import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILES,
  ALLOWED_UPLOAD_FILE_EXTENSIONS,
} from "@amzn/afxtaco-types";

import { UploadFile, UploadFileStatus } from "./types";

const FILENAME_TRUNCATE_LENGTH = 24;

/**
 * Formats a byte count into a short human-readable string (e.g. "1.2 MB").
 *
 * Uses binary (1024) units. Returns "0 B" for zero/negative inputs.
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  // Whole numbers render without a decimal; otherwise one decimal place.
  const rounded =
    Number.isInteger(value) || exponent === 0
      ? String(Math.round(value))
      : value.toFixed(1);
  return `${rounded} ${units[exponent]}`;
}

/**
 * Truncates a filename for display, preserving the file extension.
 *
 * Names at or below {@link FILENAME_TRUNCATE_LENGTH} pass through unchanged.
 * Longer names are shortened with an ellipsis while keeping the extension so
 * the user can still tell the file type, e.g.
 * `my-very-long-architecture-diagram.png` → `my-very-long-arch….png`.
 */
export function truncateFilename(
  name: string,
  maxLength: number = FILENAME_TRUNCATE_LENGTH,
): string {
  if (name.length <= maxLength) {
    return name;
  }

  const lastDot = name.lastIndexOf(".");
  // No extension (or leading dot only) — simple head truncation.
  if (lastDot <= 0) {
    return `${name.slice(0, Math.max(0, maxLength - 1))}…`;
  }

  const extension = name.slice(lastDot); // includes the dot
  const base = name.slice(0, lastDot);
  // Reserve room for the ellipsis + extension.
  const headLength = Math.max(1, maxLength - extension.length - 1);
  return `${base.slice(0, headLength)}…${extension}`;
}

/**
 * Resolves a Lucide icon component for a file based on its MIME type.
 *
 * Returns the icon *component* (not an element) so callers control sizing and
 * props. Falls back to a generic file icon for unknown types.
 *
 * TODO(fileUpload): expand the mapping once the supported file types are
 * finalized (e.g. PDFs, spreadsheets, code files).
 */
export function getFileTypeIcon(mimeType: string): LucideIcon {
  if (!mimeType) {
    return FileIcon;
  }
  if (mimeType.startsWith("image/")) {
    return FileImage;
  }
  if (mimeType.startsWith("text/")) {
    return FileText;
  }
  if (
    mimeType === "application/zip" ||
    mimeType === "application/x-zip-compressed" ||
    mimeType === "application/gzip" ||
    mimeType === "application/x-tar"
  ) {
    return FileArchive;
  }
  return FileIcon;
}

/**
 * Returns true when at least one attachment is in the {@link
 * UploadFileStatus.ERROR} state.
 *
 * The chat input uses this to block sending until the user acknowledges and
 * removes the errored chip(s). Returns false for an empty list.
 */
export function hasBlockingError(files: UploadFile[]): boolean {
  return files.some((file) => file.status === UploadFileStatus.ERROR);
}

/**
 * Result of validating a single selected file. `valid: true` means the file
 * passes all client-side checks; otherwise `reason` carries a user-facing
 * message suitable for an ERROR chip.
 */
export type FileValidationResult =
  | { valid: true }
  | { valid: false; reason: string };

/**
 * Validates a single browser File against the client-side rules that mirror
 * the server's presigned-POST validation: a non-empty size within the 25 MB
 * cap and an extension on the shared allow-list.
 *
 * This is intentionally pure (no React/state) so it can be unit-tested and
 * reused by the upload hook and the attach button.
 */
export function validateFile(file: File): FileValidationResult {
  if (file.size <= 0) {
    return { valid: false, reason: "File is empty" };
  }
  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    return {
      valid: false,
      reason: `File exceeds the ${formatFileSize(MAX_UPLOAD_FILE_SIZE_BYTES)} limit`,
    };
  }
  if (!isAllowedUploadFile(file.name)) {
    const ext = getFileExtension(file.name);
    return {
      valid: false,
      reason: ext
        ? `.${ext} files are not supported`
        : "This file type is not supported",
    };
  }
  if (hasDisallowedFilenameChars(file.name)) {
    return {
      valid: false,
      reason:
        "File name contains unsupported characters. Please rename the file.",
    };
  }
  return { valid: true };
}

/**
 * Validates that adding `incomingCount` files to the `existingCount` already
 * attached would not exceed {@link MAX_UPLOAD_FILES}. Returns a user-facing
 * reason when the limit would be exceeded.
 */
export function validateFileCount(
  existingCount: number,
  incomingCount: number,
): FileValidationResult {
  if (existingCount + incomingCount > MAX_UPLOAD_FILES) {
    return {
      valid: false,
      reason: `You can attach at most ${MAX_UPLOAD_FILES} files per message`,
    };
  }
  return { valid: true };
}

// Re-exported so callers can render the allow-list in help text/tooltips
// without importing the constants module directly.
export { ALLOWED_UPLOAD_FILE_EXTENSIONS };
